// GatepassList.jsx - Complete with Hardcoded Bilingual UI & Vector-based PDF Export
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import "./Styles/FormList.css";
// import "./Styles/NativeMobile.css";
import "../styles/LanguageTypography.css";
import gatepassStamp from '../assets/images/gpass.png';
import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, faPhone,
    faEnvelope, faMapMarkerAlt, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEye, faEyeSlash,
    faTruck, faPrint, faHashtag, faUsers
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';


// Resolve CommonJS, default and named exports across AutoTable 3/4/5.
const autoTableModule = require('jspdf-autotable');
const drawPdfTable = (doc, options) => {
    const candidates = [autoTableModule, autoTableModule.autoTable,
        autoTableModule.default, autoTableModule.default?.autoTable];
    const draw = candidates.find(candidate => typeof candidate === 'function');
    if (draw) return draw(doc, options);
    if (typeof doc.autoTable === 'function') return doc.autoTable(options);
    throw new Error('Unsupported jspdf-autotable installation. Install jspdf-autotable 3, 4 or 5.');
};


// HarfBuzz + Unicode bidi layout for Noori Nastaliq, drawn as PDF vector paths.
// Do not pre-reshape, reverse, letter-space, or justify the input strings.
const hasUrdu = value => /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(String(value ?? ''));

const scriptPromises = new Map();
let enginePromise;
const publicBase = () => (process.env.PUBLIC_URL || '').replace(/\/$/, '');

function loadScript(file, globalName) {
    if (typeof window[globalName] === 'function') return Promise.resolve();
    const url = `${publicBase()}/pdf-engine/${file}`;
    if (!scriptPromises.has(url)) {
        scriptPromises.set(url, new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            const timer = setTimeout(() => fail(), 30000);
            const fail = () => {
                clearTimeout(timer);
                script.remove();
                scriptPromises.delete(url);
                reject(new Error(`Could not load ${url}. Copy the supplied public/pdf-engine folder.`));
            };
            script.onload = () => {
                clearTimeout(timer);
                if (typeof window[globalName] !== 'function') return fail();
                resolve();
            };
            script.onerror = fail;
            document.head.appendChild(script);
        }));
    }
    return scriptPromises.get(url);
}

async function getEngine() {
    if (!enginePromise) {
        enginePromise = (async () => {
            await Promise.all([
                loadScript('hb.js', 'createHarfBuzz'),
                loadScript('hbjs.js', 'hbjs'),
                loadScript('bidi.min.js', 'bidi_js')
            ]);
            const url = `${publicBase()}/fonts/JameelNooriNastaleeq.ttf`;
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Noori font download failed (${response.status}): ${url}`);
            const bytes = await response.arrayBuffer();
            const signature = new DataView(bytes).getUint32(0);
            if (signature !== 0x00010000 && signature !== 0x4f54544f && signature !== 0x74727565) {
                throw new Error('JameelNooriNastaleeq.ttf is not a font. Copy the supplied public/fonts folder.');
            }
            const module = await window.createHarfBuzz({
                locateFile: file => `${publicBase()}/pdf-engine/${file}`
            });
            const hb = window.hbjs(module);
            const blob = hb.createBlob(bytes);
            const face = hb.createFace(blob, 0);
            const font = hb.createFont(face);
            if (!face.upem) throw new Error('Noori font could not be opened.');
            font.setScale(face.upem, face.upem);
            return { hb, font, face, blob, bidi: window.bidi_js() };
        })().catch(error => {
            enginePromise = null;
            throw error;
        });
    }
    return enginePromise;
}

async function createNooriPdfText(doc, needsUrdu = true) {
    const engine = needsUrdu ? await getEngine() : null;
    const paths = new Map();
    const lineCache = new Map();
    const factor = doc.internal.scaleFactor;
    const normalize = value => String(value ?? '').normalize('NFKC').replace(/\r\n?/g, '\n').replace(/\t/g, ' ');
    const baseDirection = text => {
        if (!engine) return 'ltr';
        return engine.bidi.getEmbeddingLevels(text).paragraphs[0]?.level % 2 ? 'rtl' : 'ltr';
    };
    const glyphPath = id => {
        if (!paths.has(id)) paths.set(id, engine.font.glyphToJson(id));
        return paths.get(id);
    };
    const plainWidth = (text, size, bold) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(size);
        return doc.getTextWidth(text);
    };

    function shapeLine(text, size, bold, direction, latinSize) {
        const key = JSON.stringify([text, size, bold, direction, latinSize]);
        if (lineCache.has(key)) return lineCache.get(key);
        const em = size / factor;
        let logicalRuns = [{ text, level: 0, start: 0, end: text.length }];
        let visualRuns = logicalRuns;
        if (engine && text) {
            const embedding = engine.bidi.getEmbeddingLevels(text, direction);
            logicalRuns = [];
            for (let start = 0; start < text.length;) {
                let end = start + 1;
                while (end < text.length && embedding.levels[end] === embedding.levels[start]) end++;
                logicalRuns.push({ text: text.slice(start, end), level: embedding.levels[start], start, end });
                start = end;
            }
            const indices = engine.bidi.getReorderedIndices(text, embedding);
            const owner = new Array(text.length);
            logicalRuns.forEach((run, id) => owner.fill(id, run.start, run.end));
            const visited = new Set();
            visualRuns = [];
            indices.forEach(index => {
                const id = owner[index];
                if (!visited.has(id)) { visited.add(id); visualRuns.push(logicalRuns[id]); }
            });
        }
        let cursor = 0;
        let minX = 0;
        let maxX = 0;
        let minY = -em * 0.78;
        let maxY = em * 0.22;
        const runs = [];
        for (const run of visualRuns) {
            if (hasUrdu(run.text)) {
                if (!engine) throw new Error('Urdu text needs the Noori font engine.');
                const buffer = engine.hb.createBuffer();
                let glyphs;
                try {
                    buffer.addText(run.text);
                    buffer.guessSegmentProperties();
                    buffer.setDirection(run.level % 2 ? 'rtl' : 'ltr');
                    buffer.setLanguage('ur');
                    buffer.setScript('Arab');
                    engine.hb.shape(engine.font, buffer);
                    glyphs = buffer.json();
                } finally { buffer.destroy(); }
                const scale = em / engine.face.upem;
                let penX = 0;
                let penY = 0;
                const positioned = glyphs.map(glyph => {
                    if (glyph.g === 0) throw new Error(`Noori font has an unsupported character in: ${run.text}`);
                    const x = cursor + (penX + glyph.dx) * scale;
                    const y = -(penY + glyph.dy) * scale;
                    const path = glyphPath(glyph.g);
                    path.forEach(command => {
                        for (let i = 0; i < command.values.length; i += 2) {
                            const px = x + command.values[i] * scale;
                            const py = y - command.values[i + 1] * scale;
                            minX = Math.min(minX, px); maxX = Math.max(maxX, px);
                            minY = Math.min(minY, py); maxY = Math.max(maxY, py);
                        }
                    });
                    penX += glyph.ax;
                    penY += glyph.ay;
                    return { x, y, path, scale };
                });
                runs.push({ glyphs: positioned });
                cursor += penX * scale;
            } else {
                let visibleText = run.text.replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '');
                if (engine && run.level % 2) {
                    visibleText = Array.from(visibleText).reverse()
                        .map(c => engine.bidi.getMirroredCharacter(c) || c).join('');
                }
                runs.push({ text: visibleText, x: cursor });
                cursor += plainWidth(visibleText, latinSize, bold);
            }
            maxX = Math.max(maxX, cursor);
        }
        const line = { text, runs, size, latinSize, bold, minX, minY, width: maxX - minX, height: maxY - minY };
        if (lineCache.size >= 3000) lineCache.clear();
        lineCache.set(key, line);
        return line;
    }

    const blockFromLines = (lines, gap) => ({
        lines, gap,
        width: Math.max(0, ...lines.map(line => line.width)),
        height: lines.reduce((sum, line) => sum + line.height, 0) + Math.max(0, lines.length - 1) * gap
    });

    function layout(value, { size = 10, maxWidth = Infinity, bold = false, direction } = {}) {
        const text = normalize(value);
        const latinSize = hasUrdu(text) ? size * 0.7 : size;
        const lines = [];
        if (!(maxWidth > 0)) throw new Error('PDF text box must have a positive width.');
        const graphemes = word => typeof Intl.Segmenter === 'function'
            ? Array.from(new Intl.Segmenter('ur', { granularity: 'grapheme' }).segment(word), s => s.segment)
            : Array.from(word);
        for (const paragraph of text.split('\n')) {
            const dir = direction || baseDirection(paragraph);
            const measure = value => shapeLine(value, size, bold, dir, latinSize);
            if (!paragraph) { lines.push(measure('')); continue; }
            let current = '';
            const flush = () => {
                if (current) lines.push(measure(current.trimEnd()));
                current = '';
            };
            // Wrap logical words first, then shape each final line from scratch.
            // Never split already-shaped glyph strings or ligatures.
            for (const token of paragraph.match(/\S+\s*|\s+/g) || []) {
                const candidate = current + token;
                if (measure(candidate.trimEnd()).width <= maxWidth) { current = candidate; continue; }
                flush();
                const word = token.trimStart();
                if (measure(word.trimEnd()).width <= maxWidth) { current = word; continue; }
                for (const cluster of graphemes(word)) {
                    if (current && measure(current + cluster).width > maxWidth) flush();
                    current += cluster;
                }
            }
            flush();
        }
        // A single unusually wide grapheme is scaled uniformly, never clipped.
        const fitted = lines.map(line => line.width > maxWidth
            ? { ...line, fitScale: maxWidth / line.width, width: maxWidth,
                height: line.height * maxWidth / line.width }
            : line);
        return blockFromLines(fitted, size / factor * 0.2);
    }

    function drawGlyph(glyph, originX, baseline, fit) {
        const commands = [];
        let currentX = 0; let currentY = 0;
        let startX = 0; let startY = 0;
        const point = (x, y) => [originX + (glyph.x + x * glyph.scale) * fit,
            baseline + (glyph.y - y * glyph.scale) * fit];
        for (const command of glyph.path) {
            const v = command.values;
            if (command.type === 'M' || command.type === 'L') {
                const p = point(v[0], v[1]);
                commands.push({ op: command.type.toLowerCase(), c: p });
                [currentX, currentY] = p;
                if (command.type === 'M') { startX = currentX; startY = currentY; }
            } else if (command.type === 'Q') {
                const control = point(v[0], v[1]);
                const end = point(v[2], v[3]);
                commands.push({ op: 'c', c: [
                    currentX + 2 / 3 * (control[0] - currentX), currentY + 2 / 3 * (control[1] - currentY),
                    end[0] + 2 / 3 * (control[0] - end[0]), end[1] + 2 / 3 * (control[1] - end[1]), ...end
                ] });
                [currentX, currentY] = end;
            } else if (command.type === 'C') {
                const end = point(v[4], v[5]);
                commands.push({ op: 'c', c: [...point(v[0], v[1]), ...point(v[2], v[3]), ...end] });
                [currentX, currentY] = end;
            } else if (command.type === 'Z') {
                commands.push({ op: 'h', c: [] });
                currentX = startX; currentY = startY;
            }
        }
        if (commands.length) doc.path(commands).fill();
    }

    function draw(block, x, top, width = block.width, align = 'left', color = [30, 41, 59]) {
        doc.saveGraphicsState();
        doc.setTextColor(...color);
        doc.setFillColor(...color);
        let y = top;
        for (const line of block.lines) {
            const fit = line.fitScale || 1;
            const left = x + (align === 'center' ? (width - line.width) / 2
                : align === 'right' ? width - line.width : 0);
            const origin = left - line.minX * fit;
            const baseline = y - line.minY * fit;
            for (const run of line.runs) {
                if (run.glyphs) run.glyphs.forEach(glyph => drawGlyph(glyph, origin, baseline, fit));
                else if (run.text) {
                    doc.setFont('helvetica', line.bold ? 'bold' : 'normal');
                    doc.setFontSize(line.latinSize * fit);
                    doc.text(run.text, origin + run.x * fit, baseline, {
                        isInputVisual: true, isOutputVisual: true, isInputRtl: false, isOutputRtl: false
                    });
                }
            }
            y += line.height + block.gap;
        }
        doc.restoreGraphicsState();
    }

    function split(block, maximumHeight) {
        const chunks = [];
        let lines = [];
        let height = 0;
        for (const line of block.lines) {
            if (line.height > maximumHeight) throw new Error('A text line is too tall for the selected page.');
            const extra = line.height + (lines.length ? block.gap : 0);
            if (lines.length && height + extra > maximumHeight) {
                chunks.push(blockFromLines(lines, block.gap)); lines = []; height = 0;
            }
            height += line.height + (lines.length ? block.gap : 0);
            lines.push(line);
        }
        if (lines.length) chunks.push(blockFromLines(lines, block.gap));
        return chunks.length ? chunks : [blockFromLines([], block.gap)];
    }
    return { layout, draw, split, baseDirection };
}


const GATEPASS_API = 'https://ourzestapp.online/oz_backend/solar_module/solar_gatepass.php';

// ============================================================
// ONLY ENGLISH AND URDU LANGUAGES
// ============================================================
const RTL_LANGUAGES = new Set(['ur']);
const SUPPORTED_LANGUAGES = new Set(['en', 'ur']);

const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    const normalized = lang.toLowerCase().split('-')[0];
    return SUPPORTED_LANGUAGES.has(normalized) ? normalized : 'en';
};

// ============================================================
// HARDCODED BILINGUAL UI DICTIONARY - ENGLISH & URDU ONLY
// ============================================================
const UI = {
    en: {
        gatepassList: 'Gate Pass List',
        formDetails: 'All gate passes details are below',
        gatepasses: 'Gate Passes',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search serial, driver, vehicle or product...',
        refresh: 'Refresh',
        loadingUserData: 'Loading your gate passes...',
        selectShop: 'Please select a shop to view gate passes',
        serial: 'Serial Number',
        party: 'Party Name',
        driver: 'Driver Name',
        phone: 'Driver Phone',
        vehicle: 'Vehicle Number',
        product: 'Product Name',
        quantity: 'Total Quantity',
        bundle: 'Total Bundle',
        checked: 'Checked By',
        approved: 'Approved By',
        date: 'Date',
        actions: 'Actions',
        addedBy: 'Created By',
        edit: 'Edit',
        view: 'View',
        delete: 'Delete',
        print: 'Print',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        serialFilter: 'Serial Number',
        partyFilter: 'Party Name',
        driverFilter: 'Driver Name',
        phoneFilter: 'Driver Phone',
        vehicleFilter: 'Vehicle Number',
        productFilter: 'Product Name',
        dateRange: 'Date Range',
        addedByFilter: 'Created By',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        gatepassDetails: 'Gate Pass Details',
        contactInfo: 'Contact Information',
        vehicleInfo: 'Vehicle Information',
        editGatepass: 'Edit Gate Pass',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this gate pass?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        gatepassReport: 'Gate Pass Report',
        gatepassDirectory: 'Gate Pass Directory',
        totalGatepasses: 'Total Gate Passes',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        noDataToExport: 'No data to export',
        serialRequired: 'Serial number is required',
        driverNameRequired: 'Driver name is required',
        vehicleRequired: 'Vehicle number is required',
        checkedByRequired: 'Checked by is required',
        approvedByRequired: 'Approved by is required',
        invalidPhone: 'Please enter a valid phone number',
        gatepassUpdated: 'Gate pass updated successfully',
        updateError: 'Error updating gate pass',
        deletedSuccess: 'Gate pass deleted successfully',
        deleteError: 'Error deleting gate pass',
        networkError: 'Network error occurred',
        dateAdded: 'Date Added',
        lastUpdated: 'Last Updated',
        selectCategory: 'Select Category',
        optional: 'Optional',
        reportGenerated: 'Report generated!',
        on: 'on',
        pages: 'page(s)',
        pdfGenerationError: 'Error generating PDF',
        filterBy: 'Filter By',
        allCategories: 'All Categories',
        regular: 'Regular',
        vip: 'VIP',
        other: 'Other',
        noOptions: 'No options found',
        allPages: 'All Pages',
        pageRange: 'Page Range',
        hideFilters: 'Hide Filters',
        showFilters: 'Show Filters',
        filters: 'Filters',
        gatepassSummary: 'Gate Pass Summary',
        system: 'System',
        notAvailable: 'N/A',
        reportFooter: 'Generated with OurZest',
        updating: 'Updating...',
        unnamed: 'Unnamed',
        noGatepasses: 'No gate passes found',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noCategoriesFound: 'No categories found',
        single: 'Single Product',
        multiple: 'Multiple Products',
        selection: 'Selection Mode',
        searchProduct: 'Search Product Name',
        selected: 'selected',
        noProducts: 'No products found',
        productRequired: 'Select at least one product',
        popup: 'Please allow pop-ups to print the gate pass',
        createdBy: 'Created By',
        dateTime: 'Date / Time',
        noProduct: 'No products',
        approvedSignature: 'Approved by',
    },
    ur: {
        gatepassList: 'گیٹ پاس کی فہرست',
        formDetails: 'تمام گیٹ پاس کی تفصیلات ذیل میں ہیں',
        gatepasses: 'گیٹ پاسز',
        shopName: 'دکان',
        noShopSelected: 'کوئی دکان منتخب نہیں۔ براہ کرم پہلے دکان منتخب کریں۔',
        switchShop: 'دکان تبدیل کریں',
        loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
        noShops: 'کوئی دکان دستیاب نہیں',
        current: 'موجودہ',
        searchPlaceholder: 'سیریل، ڈرائیور، گاڑی یا پروڈکٹ تلاش کریں...',
        refresh: 'ریفریش',
        loadingUserData: 'آپ کا ڈیٹا لوڈ ہو رہا ہے...',
        selectShop: 'براہ کرم گیٹ پاس دیکھنے کے لیے دکان منتخب کریں',
        serial: 'سیریل نمبر',
        party: 'پارٹی کا نام',
        driver: 'ڈرائیور کا نام',
        phone: 'ڈرائیور کا فون نمبر',
        vehicle: 'گاڑی کا نمبر',
        product: 'پروڈکٹ کا نام',
        quantity: 'کل مقدار',
        bundle: 'کل بنڈل',
        checked: 'چیک کرنے والا',
        approved: 'منظور کرنے والا',
        date: 'تاریخ',
        actions: 'اعمال',
        addedBy: 'بنانے والا',
        edit: 'ترمیم',
        view: 'دیکھیں',
        delete: 'حذف',
        print: 'پرنٹ کریں',
        serialNo: 'شمار',
        showing: 'دکھا رہا ہے',
        of: 'میں سے',
        previous: 'پچھلا',
        next: 'اگلا',
        all: 'سب',
        serialFilter: 'سیریل نمبر',
        partyFilter: 'پارٹی کا نام',
        driverFilter: 'ڈرائیور کا نام',
        phoneFilter: 'ڈرائیور کا فون',
        vehicleFilter: 'گاڑی کا نمبر',
        productFilter: 'پروڈکٹ کا نام',
        dateRange: 'تاریخ کی حد',
        addedByFilter: 'بنانے والا',
        startDate: 'شروع کی تاریخ',
        endDate: 'آخری تاریخ',
        exportToPDF: 'پی ڈی ایف ایکسپورٹ',
        orientation: 'اورینٹیشن',
        portrait: 'عمودی',
        landscape: 'افقی',
        pageSize: 'صفحہ سائز',
        a4: 'A4',
        letter: 'لیٹر',
        legal: 'لیگل',
        a5: 'A5',
        gatepassDetails: 'گیٹ پاس کی تفصیلات',
        contactInfo: 'رابطہ کی معلومات',
        vehicleInfo: 'گاڑی کی معلومات',
        editGatepass: 'گیٹ پاس میں ترمیم',
        saveChanges: 'تبدیلیاں محفوظ کریں',
        cancel: 'منسوخ',
        confirmDelete: 'حذف کی تصدیق',
        deleteMessage: 'کیا آپ اس گیٹ پاس کو حذف کرنا چاہتے ہیں؟',
        deleteWarning: 'یہ عمل واپس نہیں کیا جا سکتا۔',
        yesDelete: 'ہاں، حذف کریں',
        noCancel: 'نہیں، منسوخ',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        close: 'بند کریں',
        gatepassReport: 'گیٹ پاس رپورٹ',
        gatepassDirectory: 'گیٹ پاس ڈائریکٹری',
        totalGatepasses: 'کل گیٹ پاسز',
        generated: 'تیار کردہ',
        reportId: 'رپورٹ آئی ڈی',
        generatedBy: 'تیار کردہ',
        shopCode: 'دکان کوڈ',
        filtersApplied: 'فلٹرز لگائے گئے',
        search: 'تلاش',
        from: 'سے',
        to: 'تک',
        page: 'صفحہ',
        noDataToExport: 'ایکسپورٹ کرنے کے لیے کوئی ڈیٹا نہیں',
        serialRequired: 'سیریل نمبر ضروری ہے',
        driverNameRequired: 'ڈرائیور کا نام ضروری ہے',
        vehicleRequired: 'گاڑی کا نمبر ضروری ہے',
        checkedByRequired: 'چیک کرنے والا ضروری ہے',
        approvedByRequired: 'منظور کرنے والا ضروری ہے',
        invalidPhone: 'براہ کرم درست فون نمبر درج کریں',
        gatepassUpdated: 'گیٹ پاس کامیابی سے اپ ڈیٹ ہو گیا',
        updateError: 'گیٹ پاس اپ ڈیٹ کرنے میں خرابی',
        deletedSuccess: 'گیٹ پاس کامیابی سے حذف ہو گیا',
        deleteError: 'گیٹ پاس حذف کرنے میں خرابی',
        networkError: 'نیٹ ورک کی خرابی',
        dateAdded: 'شامل کرنے کی تاریخ',
        lastUpdated: 'آخری اپ ڈیٹ',
        selectCategory: 'کیٹیگری منتخب کریں',
        optional: '',
        reportGenerated: 'رپورٹ تیار ہو گئی',
        on: 'پر',
        pages: 'صفحات',
        pdfGenerationError: 'پی ڈی ایف بنانے میں خرابی',
        filterBy: 'فلٹر کریں',
        allCategories: 'تمام کیٹیگریز',
        regular: 'عام',
        vip: 'وی آئی پی',
        other: 'دیگر',
        noOptions: 'کوئی آپشن نہیں',
        allPages: 'تمام صفحات',
        pageRange: 'صفحات کی حد',
        hideFilters: 'فلٹرز چھپائیں',
        showFilters: 'فلٹرز دکھائیں',
        filters: 'فلٹرز',
        gatepassSummary: 'گیٹ پاس کا خلاصہ',
        system: 'سسٹم',
        notAvailable: 'دستیاب نہیں',
        reportFooter: 'آور زیسٹ کے ذریعے تیار کردہ',
        updating: 'اپ ڈیٹ ہو رہا ہے...',
        unnamed: 'بے نام',
        noGatepasses: 'کوئی گیٹ پاس نہیں ملا',
        shopSwitched: 'دکان کامیابی سے تبدیل ہو گئی',
        shopSwitchFailed: 'دکان تبدیل نہیں ہو سکی',
        noCategoriesFound: 'کوئی کیٹیگری نہیں ملی',
        single: 'سنگل پروڈکٹ',
        multiple: 'متعدد پروڈکٹس',
        selection: 'انتخاب کا طریقہ',
        searchProduct: 'پروڈکٹ کا نام تلاش کریں',
        selected: 'منتخب',
        noProducts: 'کوئی پروڈکٹ نہیں ملی',
        productRequired: 'کم از کم ایک پروڈکٹ منتخب کریں',
        popup: 'گیٹ پاس پرنٹ کرنے کے لیے پاپ اپس کی اجازت دیں',
        createdBy: 'بنانے والا',
        dateTime: 'تاریخ / وقت',
        noProduct: 'کوئی پروڈکٹ نہیں',
        approvedSignature: 'منظور کنندہ',
    }
};

// ============================================================
// TOAST NOTIFICATION - POSITION BASED ON LANGUAGE
// ============================================================
const ToastNotification = ({ toast, setToast, t, isRTL }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return t.success || 'Success';
            case 'error': return t.error || 'Error';
            case 'warning': return t.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const positionStyle = isRTL
        ? { bottom: '20px', left: '20px', right: 'auto' }
        : { bottom: '20px', right: '20px', left: 'auto' };

    return (
        <div className={`ozlistui-toast-notification ${toast.type}`} style={positionStyle}>
            <div className="ozlistui-toast-content">
                <div className="ozlistui-toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozlistui-toast-text">
                    <div className="ozlistui-toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozlistui-toast-message">{toast.message}</div>
                </div>
                <button className="ozlistui-toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ============================================================
// SHOP SELECTOR MODAL
// ============================================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, t, loading, isRTL }) => {
    if (!isOpen) return null;

    return (
        <div className="ozlistui-modal-overlay" onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                <div className="ozlistui-modal-header">
                    <h3>{t.switchShop}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{t.loadingShops}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{t.noShops}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{t.current}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// CUSTOM DROPDOWN COMPONENT
// ============================================================
const CustomDropdown = ({ label, icon, value, onChange, options, t, disabled = false, isRTL = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const menuRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        setSearchTerm(displayValue);
    }, [displayValue]);

    const filteredOptions = searchTerm.trim() === ''
        ? options
        : options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : prev);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.ozlistui-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    return (
        <div className="ozlistui-custom-dropdown-wrapper" ref={wrapperRef}>
            <div className="ozlistui-custom-input-wrapper">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        setHighlightedIndex(0);
                        if (!options.find(opt => opt.label.toLowerCase() === e.target.value.toLowerCase())) {
                            onChange('');
                        }
                    }}
                    onClick={() => {
                        if (!disabled) {
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onFocus={() => {
                        if (!disabled) {
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                    dir={isRTL ? 'rtl' : 'ltr'}
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                    {!isRTL && t.optional && <span> ({t.optional})</span>}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu" ref={menuRef} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''} ${highlightedIndex === index ? 'ozlistui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            {t?.noOptions || 'No options found'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================
// PRINT GATEPASS FUNCTION
// ============================================================
// ============================================================
// PRINT GATEPASS FUNCTION WITH NOORI NASTALIQ FONT & STAMP
// ============================================================
const printGatepass = (gatepass, currentShop, user, currentLanguage) => {
    const printWindow = window.open('', '_blank', 'width=420,height=720');
    if (!printWindow) {
        alert('Please allow pop-ups to print the gate pass');
        return;
    }

    const escapeHtml = (value) => {
        if (!value) return '';
        return String(value).replace(/[&<>'"]/g, (char) => {
            const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' };
            return map[char] || char;
        });
    };

    const t = UI[currentLanguage] || UI.en;
    const isRTL = currentLanguage === 'ur';

    const productNames = (gatepass.products || []).map(product => product.product_name).filter(Boolean);

    const detailRows = [
        [t.party, gatepass.party_name || '-'],
        [t.driver, gatepass.driver_name || '-'],
        [t.phone, gatepass.driver_phone || '-'],
        [t.vehicle, gatepass.vehicle_no || '-'],
        [t.selection, gatepass.selection_mode === 'single' ? t.single : t.multiple],
        [t.quantity, Number(gatepass.total_quantity || 0).toLocaleString()],
        [t.bundle, Number(gatepass.total_bundle || 0).toLocaleString()],
        [t.createdBy, gatepass.username || '-'],
        [t.dateTime, gatepass.created_at ? new Date(gatepass.created_at).toLocaleString(currentLanguage === 'ur' ? 'ur-PK' : 'en-GB') : '-']
    ];

    const rowsHtml = detailRows.map(([label, value]) => 
        `<div class="detail"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`
    ).join('');

    const productsHtml = productNames.length
        ? productNames.map((name, index) => `<div class="product"><b>${index + 1}.</b> ${escapeHtml(name)}</div>`).join('')
        : `<div class="product">${escapeHtml(t.noProduct)}</div>`;

    const businessName = currentShop?.shop_name || 'OurZest Store';
    const businessCode = currentShop?.shop_code || '';

    // Get font family based on language
    const getFontFamily = () => {
        if (currentLanguage === 'ur') {
            return "'Jameel Noori Nastaleeq', 'Noto Nastaliq Urdu', 'Noto Naskh Arabic', serif";
        }
        return "Arial, Helvetica, sans-serif";
    };

    const getFontSize = () => {
        if (currentLanguage === 'ur') return '16px';
        return '13px';
    };

    const getBusinessFontSize = () => {
        if (currentLanguage === 'ur') return '22px';
        return '19px';
    };

    const getLineHeight = () => {
        if (currentLanguage === 'ur') return '2.0';
        return '1.28';
    };

    // Get stamp image URL - use the imported gatepassStamp
    const stampImage = gatepassStamp || '/assets/images/gpass.png';

    printWindow.document.write(`<!doctype html><html lang="${escapeHtml(currentLanguage)}" dir="${isRTL ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>${escapeHtml(gatepass.serial_number)}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;600;700&family=Noto+Nastaliq+Urdu:wght@400;600;700&display=swap" rel="stylesheet">
    <style>
        @page { size: 76mm auto; margin: 3mm; }
        * { box-sizing: border-box; }
        html, body { width: 70mm; margin: 0; padding: 0; color: #000; background: #fff; }
        body { 
            font-family: ${getFontFamily()}; 
            font-size: ${getFontSize()}; 
            font-weight: 700; 
            line-height: ${getLineHeight()}; 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact; 
        }
        .receipt { width: 70mm; padding: 1mm; }
        .business-header { padding-bottom: 6px; margin-bottom: 6px; border-bottom: 3px double #000; text-align: center; }
        .business-name { margin: 0; font-size: ${getBusinessFontSize()}; font-weight: 900; line-height: 1.25; text-transform: uppercase; overflow-wrap: anywhere; }
        .business-code { margin-top: 2px; font-size: 11px; font-weight: 700; line-height: 1.25; overflow-wrap: anywhere; color: #333; }
        .gatepass-title-row { display: flex; align-items: center; justify-content: center; width: 100%; min-height: 27mm; margin: 3px 0 4px; overflow: visible; }
        .stamp-wrap { display: flex; align-items: center; justify-content: center; width: 38mm; height: 26mm; overflow: visible; }
        .gatepass-stamp { display: block; width: 35mm; max-width: 35mm; max-height: 25mm; object-fit: contain; opacity: .92; transform: rotate(-10deg); transform-origin: center; filter: grayscale(1) contrast(1.65); mix-blend-mode: multiply; }
        .serial { margin: 5px 0 8px; padding: 7px 3px; border: 2px solid #000; text-align: center; font-size: ${currentLanguage === 'ur' ? '20px' : '17px'}; font-weight: 900; }
        .detail { display: grid; grid-template-columns: 42% 58%; gap: 3px; padding: ${currentLanguage === 'ur' ? '6px 1px' : '5px 1px'}; border-bottom: 1.5px dashed #000; }
        .detail span, .detail strong { font-weight: 900; overflow-wrap: anywhere; }
        .detail strong { text-align: ${isRTL ? 'left' : 'right'}; }
        .products-section { margin-top: 12px; }
        .product { padding: ${currentLanguage === 'ur' ? '6px 2px' : '5px 2px'}; border-bottom: 1.5px solid #000; font-size: ${currentLanguage === 'ur' ? '17px' : '15px'}; font-weight: 900; overflow-wrap: anywhere; }
        .signature-section { margin-top: 15px; padding-top: 10px; border-top: 2px double #000; display: flex; justify-content: flex-end; padding-right: 10px; }
        .signature-box { text-align: center; width: 120px; }
        .signature-line { border-bottom: 1.5px solid #000; padding-bottom: 3px; margin-bottom: 3px; height: 25px; }
        .signature-label { font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.5px; }
        .footer { margin-top: 9px; padding-top: 5px; border-top: 2px double #000; text-align: center; font-size: 11px; font-weight: 900; }
        
        /* Urdu specific styling */
        html[lang="ur"] .detail { line-height: 2.0; }
        html[lang="ur"] .product { line-height: 1.9; }
        html[lang="ur"] .business-name { font-size: 22px; }
        
        @media print { html, body, .receipt { width: 70mm; } }
    </style></head><body><main class="receipt">
        <header class="business-header">
            <div class="business-name">${escapeHtml(businessName)}</div>
            ${businessCode ? `<div class="business-code">Code: ${escapeHtml(businessCode)}</div>` : ''}
        </header>
        <div class="gatepass-title-row"><div class="stamp-wrap"><img src="${escapeHtml(stampImage)}" alt="Gate Pass Stamp" class="gatepass-stamp" onerror="this.style.display='none'" /></div></div>
        <div class="serial">${escapeHtml(gatepass.serial_number)}</div>
        ${rowsHtml}
        <div class="products-section">${productsHtml}</div>
        <div class="signature-section">
            <div class="signature-box">
                <div class="signature-line">&nbsp;</div>
                <div class="signature-label">${escapeHtml(t.approvedSignature)}</div>
            </div>
        </div>
        <div class="footer">*** Powered by OurZest PVT LTD ***</div>
    </main><script>window.addEventListener('load',function(){setTimeout(function(){window.print();},250);});<\/script></body></html>`);
    printWindow.document.close();
    printWindow.focus();
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const GatepassList = () => {
    const auth = useAuth();
    const { user, currentShop, switchShop, getShopsForSwitch } = auth;

    // ============================================================
    // LANGUAGE DETECTION
    // ============================================================
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return normalizeLanguage(
            auth?.currentLanguage ||
            auth?.language ||
            user?.language ||
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') ||
            'en'
        );
    });

    const isRTL = currentLanguage === 'ur';
    const t = UI[currentLanguage] || UI.en;

    const [gatepasses, setGatepasses] = useState([]);
    const [filteredGatepasses, setFilteredGatepasses] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGatepass, setSelectedGatepass] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [gatepassToDelete, setGatepassToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // PDF Options
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);
    const [pdfLoading, setPdfLoading] = useState(false);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('wide');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0, width: 0, height: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    const widthModes = {
        narrow: { width: 400, label: 'Narrow', icon: faMinus },
        medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
        wide: { width: 850, label: 'Wide', icon: faPlus },
        full: { width: '95vw', label: 'Full Width', icon: faExpand },
        fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
    };

    // ============================================================
    // THEME & DARK MODE
    // ============================================================
    const [darkMode, setDarkMode] = useState(() => {
        return document.body.classList.contains('dark-mode') || localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    // ============================================================
    // TOAST
    // ============================================================
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // ============================================================
    // DARK MODE SYNC
    // ============================================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isBodyDark = document.body.classList.contains('dark-mode') || document.body.classList.contains('ozlistui-dark');
            const isStorageDark = localStorage.getItem('ourzest_darkMode') === 'true';
            const shouldBeDark = isBodyDark || isStorageDark;
            setDarkMode(shouldBeDark);
            const container = document.querySelector('.ozlistui-container');
            if (container) {
                if (shouldBeDark) container.classList.add('ozlistui-dark');
                else container.classList.remove('ozlistui-dark');
            }
        };

        syncDarkMode();

        const observer = new MutationObserver(() => syncDarkMode());
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') syncDarkMode();
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            observer.disconnect();
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // ============================================================
    // THEME SYNC
    // ============================================================
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
            document.documentElement.style.setProperty('--ozlistui-primary', color);
            document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
            document.documentElement.style.setProperty('--ozlistui-primary-light', color);
            setCurrentTheme(color);
        };

        applyTheme(currentTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') applyTheme(e.newValue || '#6366f1');
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) applyTheme(event.detail.theme);
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [currentTheme]);

    // ============================================================
    // LANGUAGE SYNC
    // ============================================================
    useEffect(() => {
        const contextLanguage = normalizeLanguage(
            auth?.currentLanguage ||
            auth?.language ||
            user?.language ||
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') ||
            'en'
        );

        if (contextLanguage !== currentLanguage) {
            setCurrentLanguage(contextLanguage);
        }
    }, [auth?.currentLanguage, auth?.language, user?.language, user?.preferred_language]);

    useEffect(() => {
        const handleLanguageChange = (event) => {
            const newLang = normalizeLanguage(event?.detail?.language || event?.detail?.lang);
            if (newLang && newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
            }
        };

        window.addEventListener('languageChanged', handleLanguageChange);
        window.addEventListener('ourzest:language-change', handleLanguageChange);
        window.addEventListener('storage', (e) => {
            if (e.key === 'ourzest_language') {
                const newLang = normalizeLanguage(e.newValue);
                if (newLang !== currentLanguage) setCurrentLanguage(newLang);
            }
        });

        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
            window.removeEventListener('ourzest:language-change', handleLanguageChange);
        };
    }, [currentLanguage]);

    // ============================================================
    // RTL DIRECTION
    // ============================================================
    useEffect(() => {
        document.documentElement.lang = currentLanguage;
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
        const container = document.querySelector('.ozlistui-container');
        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }
    }, [currentLanguage, isRTL]);

    // ============================================================
    // EDIT FORM STATE
    // ============================================================
    const [editFormData, setEditFormData] = useState({
        serial_number: '',
        party_name: '',
        driver_name: '',
        driver_phone: '',
        vehicle_no: '',
        selection_mode: 'single',
        selected_products: [],
        total_quantity: '',
        total_bundle: '',
        checked_by: '',
        approved_by: ''
    });
    const [editProductSearch, setEditProductSearch] = useState('');
    const [editFormErrors, setEditFormErrors] = useState({});

    // ============================================================
    // PAGINATION
    // ============================================================
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // ============================================================
    // FILTER STATES
    // ============================================================
    const [filterBy, setFilterBy] = useState('');
    const [serialFilter, setSerialFilter] = useState('');
    const [partyFilter, setPartyFilter] = useState('');
    const [driverFilter, setDriverFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [vehicleFilter, setVehicleFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // ============================================================
    // OPTIONS (Hardcoded bilingual)
    // ============================================================
    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: t.portrait || 'Portrait' },
        { value: 'landscape', label: t.landscape || 'Landscape' }
    ], [t]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: t.a4 || 'A4' },
        { value: 'letter', label: t.letter || 'Letter' },
        { value: 'legal', label: t.legal || 'Legal' },
        { value: 'a5', label: t.a5 || 'A5' }
    ], [t]);

    const filterByOptions = useMemo(() => [
        { value: '', label: t.all || 'All' },
        { value: 'serial', label: t.serialFilter || 'Serial Number' },
        { value: 'party', label: t.partyFilter || 'Party Name' },
        { value: 'driver', label: t.driverFilter || 'Driver Name' },
        { value: 'phone', label: t.phoneFilter || 'Driver Phone' },
        { value: 'vehicle', label: t.vehicleFilter || 'Vehicle Number' },
        { value: 'product', label: t.productFilter || 'Product Name' },
        { value: 'date', label: t.dateRange || 'Date Range' },
        { value: 'added_by', label: t.addedByFilter || 'Created By' }
    ], [t]);

    const selectionModeOptions = useMemo(() => [
        { value: 'single', label: t.single || 'Single Product' },
        { value: 'multiple', label: t.multiple || 'Multiple Products' }
    ], [t]);

    // ============================================================
    // READ API HELPER
    // ============================================================
    const readApiJson = async (response) => {
        const raw = await response.text();
        let data;
        try {
            data = raw ? JSON.parse(raw.replace(/^\uFEFF/, '')) : null;
        } catch (_) {
            throw new Error(`Server returned invalid JSON (${response.status})`);
        }
        if (!response.ok) throw new Error(data?.message || `Server error: ${response.status}`);
        if (!data) throw new Error('Server returned an empty response');
        return data;
    };

    // ============================================================
    // FETCH GATEPASSES
    // ============================================================
    const fetchGatepasses = useCallback(async () => {
        if (!user?.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop?.shop_id) {
            if (isMountedRef.current) {
                showToast('error', t.selectShop || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }

        if (isMountedRef.current) setLoading(true);
        try {
            const [listData, productData] = await Promise.all([
                fetch(GATEPASS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'list',
                        username: user.username,
                        shop_id: currentShop.shop_id,
                        language: currentLanguage
                    })
                }),
                fetch(GATEPASS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_products',
                        username: user.username,
                        shop_id: currentShop.shop_id,
                        language: currentLanguage
                    })
                })
            ]);

            const listDataJson = await readApiJson(listData);
            const productDataJson = await readApiJson(productData);

            if (listDataJson.status !== 'success') throw new Error(listDataJson.message || 'Could not load gate passes');
            if (productDataJson.status !== 'success') throw new Error(productDataJson.message || 'Could not load products');

            if (isMountedRef.current) {
                const records = Array.isArray(listDataJson.gatepasses) ? listDataJson.gatepasses : [];
                setGatepasses(records);
                setFilteredGatepasses(records);
                setProducts(productDataJson.products || []);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error fetching gate passes:', error);
            if (isMountedRef.current) showToast('error', error.message || t.networkError || 'Network error occurred');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user?.username, currentShop?.shop_id, currentLanguage, showToast, t]);

    // ============================================================
    // FETCH SHOPS
    // ============================================================
    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    // ============================================================
    // INITIAL LOAD
    // ============================================================
    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchGatepasses();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id, fetchGatepasses, fetchAvailableShops]);

    // ============================================================
    // APPLY FILTERS
    // ============================================================
    const applyFilters = useCallback(() => {
        let filtered = [...gatepasses];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(gatepass =>
                gatepass.serial_number?.toLowerCase().includes(term) ||
                gatepass.party_name?.toLowerCase().includes(term) ||
                gatepass.driver_name?.toLowerCase().includes(term) ||
                gatepass.driver_phone?.toLowerCase().includes(term) ||
                gatepass.vehicle_no?.toLowerCase().includes(term) ||
                gatepass.checked_by?.toLowerCase().includes(term) ||
                gatepass.approved_by?.toLowerCase().includes(term) ||
                gatepass.username?.toLowerCase().includes(term) ||
                (gatepass.products || []).some(p => p.product_name?.toLowerCase().includes(term))
            );
        }
        if (filterBy === 'serial' && serialFilter.trim()) {
            const term = serialFilter.toLowerCase();
            filtered = filtered.filter(gatepass => gatepass.serial_number?.toLowerCase().includes(term));
        }
        if (filterBy === 'party' && partyFilter.trim()) {
            const term = partyFilter.toLowerCase();
            filtered = filtered.filter(gatepass => gatepass.party_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'driver' && driverFilter.trim()) {
            const term = driverFilter.toLowerCase();
            filtered = filtered.filter(gatepass => gatepass.driver_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'phone' && phoneFilter.trim()) {
            const term = phoneFilter.toLowerCase();
            filtered = filtered.filter(gatepass => gatepass.driver_phone?.toLowerCase().includes(term));
        }
        if (filterBy === 'vehicle' && vehicleFilter.trim()) {
            const term = vehicleFilter.toLowerCase();
            filtered = filtered.filter(gatepass => gatepass.vehicle_no?.toLowerCase().includes(term));
        }
        if (filterBy === 'product' && productFilter.trim()) {
            const term = productFilter.toLowerCase();
            filtered = filtered.filter(gatepass =>
                (gatepass.products || []).some(p => p.product_name?.toLowerCase().includes(term))
            );
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(gatepass => gatepass.username?.toLowerCase().includes(term));
        }
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(gatepass => {
                if (!gatepass.created_at) return false;
                const gatepassDate = new Date(gatepass.created_at);
                gatepassDate.setHours(0, 0, 0, 0);
                return gatepassDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(gatepass => {
                if (!gatepass.created_at) return false;
                const gatepassDate = new Date(gatepass.created_at);
                return gatepassDate <= end;
            });
        }
        setFilteredGatepasses(filtered);
        setCurrentPage(1);
    }, [gatepasses, searchTerm, filterBy, serialFilter, partyFilter, driverFilter, phoneFilter, vehicleFilter, productFilter, addedByFilter, startDate, endDate]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    // ============================================================
    // HELPER FUNCTIONS
    // ============================================================
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    }, []);

    const formatDateTime = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleString(currentLanguage === 'ur' ? 'ur-PK' : 'en-GB');
        } catch { return dateString; }
    }, [currentLanguage]);

    const getProductsDisplay = useCallback((products) => {
        if (!products || !products.length) return '-';
        return products.map(p => p.product_name).join(', ');
    }, []);

    const getTodayDate = useCallback(() => new Date().toISOString().split('T')[0], []);

    // ============================================================
    // PAGINATION
    // ============================================================
    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredGatepasses.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentGatepasses = filteredGatepasses.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentGatepasses };
    }, [filteredGatepasses, currentPage, itemsPerPage]);

    // ============================================================
    // MODAL DRAG / RESIZE
    // ============================================================
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
        }
        requestAnimationFrame(() => {
            if (!modalRef.current) return;
            const rect = modalRef.current.getBoundingClientRect();
            setModalSize({ width: rect.width, height: rect.height });
            centerModal();
        });
    }, [widthModes, centerModal]);

    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .ozlistui-width-btn, .ozlistui-close-btn')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            width: rect.width,
            height: rect.height
        };
        modalRef.current.style.transition = 'none';
    }, []);

    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX, y: e.clientY,
            width: rect.width, height: rect.height,
            left: rect.left, top: rect.top,
            minWidth: 350, minHeight: 400,
            maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - dragStart.current.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - dragStart.current.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }

            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };

        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) modalRef.current.style.transition = '';
            }
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing]);

    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('wide'); setIsModalInitialized(true); }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // ============================================================
    // HANDLE ACTIONS
    // ============================================================
    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', t.shopSwitched || 'Shop switched successfully');
                setCurrentPage(1);
                setShowShopSelector(false);
                fetchGatepasses();
            } else {
                showToast('error', t.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', t.shopSwitchFailed || 'Failed to switch shop');
        }
    }, [switchShop, showToast, t, fetchGatepasses]);

    const handleView = useCallback((gatepass) => {
        setSelectedGatepass(gatepass);
        setShowViewModal(true);
    }, []);

    const handlePrint = useCallback((gatepass) => {
        printGatepass(gatepass, currentShop, user, currentLanguage);
    }, [currentShop, user, currentLanguage]);

    const handleEdit = useCallback((gatepass) => {
        const sourceGatepass = gatepass.__source || gatepass;
        setSelectedGatepass(gatepass);
        setEditFormData({
            serial_number: sourceGatepass.serial_number || '',
            party_name: sourceGatepass.party_name || '',
            driver_name: sourceGatepass.driver_name || '',
            driver_phone: sourceGatepass.driver_phone || '',
            vehicle_no: sourceGatepass.vehicle_no || '',
            selection_mode: sourceGatepass.selection_mode || 'single',
            selected_products: (sourceGatepass.products || []).map(p => Number(p.product_id)),
            total_quantity: sourceGatepass.total_quantity || '',
            total_bundle: sourceGatepass.total_bundle || '',
            checked_by: sourceGatepass.checked_by || '',
            approved_by: sourceGatepass.approved_by || ''
        });
        setEditProductSearch('');
        setEditFormErrors({});
        setShowEditModal(true);
    }, []);

    const toggleEditProduct = useCallback((id) => {
        setEditFormData(prev => {
            const exists = prev.selected_products.includes(id);
            const selected_products = prev.selection_mode === 'single'
                ? [id]
                : exists
                    ? prev.selected_products.filter(v => v !== id)
                    : [...prev.selected_products, id];
            return { ...prev, selected_products };
        });
    }, []);

    const filteredEditProducts = useMemo(() => {
        const term = editProductSearch.trim().toLowerCase();
        return term ? products.filter(product => product.product_name?.toLowerCase().includes(term)) : products;
    }, [editProductSearch, products]);

    const handleDeleteClick = useCallback((gatepassId) => {
        const gatepass = gatepasses.find(g => g.gatepass_id === gatepassId);
        if (!gatepass) return;
        if (gatepass.username !== user?.username) {
            showToast('error', 'You can only delete gate passes you created');
            return;
        }
        setGatepassToDelete(gatepassId);
        setShowDeleteModal(true);
    }, [gatepasses, user, showToast]);

    const handleDelete = useCallback(async () => {
        if (!gatepassToDelete || !currentShop) return;
        const gatepass = gatepasses.find(g => g.gatepass_id === gatepassToDelete);
        if (!gatepass || gatepass.username !== user?.username) {
            showToast('error', 'You can only delete your own gate passes');
            setShowDeleteModal(false);
            setGatepassToDelete(null);
            return;
        }
        try {
            const response = await fetch(GATEPASS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete',
                    gatepass_id: gatepassToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });
            const data = await readApiJson(response);
            if (data.status === 'success') {
                showToast('success', t.deletedSuccess || 'Gate pass deleted successfully');
                fetchGatepasses();
                setShowDeleteModal(false);
                setGatepassToDelete(null);
            } else {
                showToast('error', data.message || t.deleteError || 'Error deleting gate pass');
            }
        } catch (error) {
            console.error('Error deleting gate pass:', error);
            showToast('error', t.deleteError || 'Error deleting gate pass');
        }
    }, [gatepassToDelete, currentShop, gatepasses, user, currentLanguage, t, showToast, fetchGatepasses]);

    const handleUpdateGatepass = useCallback(async (e) => {
        e.preventDefault();
        if (!selectedGatepass || !currentShop) return;

        const errors = {};
        if (!editFormData.serial_number.trim()) errors.serial_number = t.serialRequired || 'Serial number is required';
        if (!editFormData.driver_name.trim()) errors.driver_name = t.driverNameRequired || 'Driver name is required';
        if (!editFormData.vehicle_no.trim()) errors.vehicle_no = t.vehicleRequired || 'Vehicle number is required';
        if (!editFormData.checked_by.trim()) errors.checked_by = t.checkedByRequired || 'Checked by is required';
        if (!editFormData.approved_by.trim()) errors.approved_by = t.approvedByRequired || 'Approved by is required';
        if (editFormData.driver_phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(editFormData.driver_phone)) {
            errors.driver_phone = t.invalidPhone || 'Please enter a valid phone number';
        }
        if (!editFormData.selected_products.length) errors.selected_products = t.productRequired || 'Select at least one product';

        if (Object.keys(errors).length > 0) { setEditFormErrors(errors); return; }

        setEditLoading(true);
        try {
            const response = await fetch(GATEPASS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update',
                    gatepass_id: selectedGatepass.gatepass_id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage,
                    serial_number: editFormData.serial_number,
                    party_name: editFormData.party_name || currentShop?.shop_name || '',
                    driver_name: editFormData.driver_name,
                    driver_phone: editFormData.driver_phone,
                    vehicle_no: editFormData.vehicle_no,
                    selection_mode: editFormData.selection_mode,
                    selected_products: editFormData.selected_products,
                    total_quantity: Number(editFormData.total_quantity || 0),
                    total_bundle: Number(editFormData.total_bundle || 0),
                    checked_by: editFormData.checked_by,
                    approved_by: editFormData.approved_by
                })
            });
            const data = await readApiJson(response);
            if (data.status === 'success') {
                showToast('success', t.gatepassUpdated || 'Gate pass updated successfully');
                setShowEditModal(false);
                fetchGatepasses();
            } else {
                showToast('error', data.message || t.updateError || 'Error updating gate pass');
            }
        } catch (error) {
            console.error('Error updating gate pass:', error);
            showToast('error', t.updateError || 'Error updating gate pass');
        } finally {
            setEditLoading(false);
        }
    }, [selectedGatepass, currentShop, editFormData, user, currentLanguage, t, showToast, fetchGatepasses]);

    const handlePageChange = useCallback((page) => {
        if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page);
    }, [pagination.totalPages]);

    const resetFilters = useCallback(() => {
        setFilterBy('');
        setSerialFilter('');
        setPartyFilter('');
        setDriverFilter('');
        setPhoneFilter('');
        setVehicleFilter('');
        setProductFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    }, []);

    // ============================================================
    // PDF EXPORT — VECTOR-BASED (jsPDF + autoTable), NOT html2canvas
    // ============================================================
    const exportToPDF = useCallback(async () => {
        if (!filteredGatepasses?.length) {
            showToast('warning', t.noDataToExport || 'No data to export');
            return;
        }
        setPdfLoading(true);
        try {
            const urdu = currentLanguage === 'ur';
            const doc = new jsPDF({
                orientation: pdfOrientation === 'landscape' ? 'landscape' : 'portrait',
                unit: 'mm', compress: true,
                format: ['a4', 'a5', 'letter', 'legal'].includes(pdfPageSize) ? pdfPageSize : 'a4'
            });
            doc.setR2L(false);
            const labels = urdu ? {
                title: 'گیٹ پاس کی فہرست', total: 'کل گیٹ پاس', generated: 'تیاری کی تاریخ',
                report: 'رپورٹ نمبر', code: 'دکان کا کوڈ', page: 'صفحہ'
            } : {
                title: 'Gate Pass Directory', total: 'Total gate passes', generated: 'Generated',
                report: 'Report ID', code: 'Shop code', page: 'Page'
            };
            const columns = [
                { key: 'serial', title: urdu ? 'شمار' : 'No.', weight: 5 },
                { key: 'serialNo', title: urdu ? 'سیریل نمبر' : 'Serial No.', weight: 11 },
                { key: 'party', title: urdu ? 'پارٹی کا نام' : 'Party', weight: 12 },
                { key: 'driver', title: urdu ? 'ڈرائیور' : 'Driver', weight: 12 },
                { key: 'phone', title: urdu ? 'فون' : 'Phone', weight: 10 },
                { key: 'vehicle', title: urdu ? 'گاڑی کا نمبر' : 'Vehicle', weight: 10 },
                { key: 'product', title: urdu ? 'پروڈکٹ' : 'Product', weight: 14 },
                { key: 'quantity', title: urdu ? 'مقدار' : 'Qty', weight: 8 },
                { key: 'bundle', title: urdu ? 'بنڈل' : 'Bundle', weight: 8 },
                { key: 'checked', title: urdu ? 'چیک کرنے والا' : 'Checked', weight: 10 },
                { key: 'approved', title: urdu ? 'منظور کرنے والا' : 'Approved', weight: 10 }
            ];
            const rows = filteredGatepasses.map((gatepass, index) => ({
                serial: String(index + 1),
                serialNo: String(gatepass.serial_number || '-'),
                party: String(gatepass.party_name || '-'),
                driver: String(gatepass.driver_name || '-'),
                phone: String(gatepass.driver_phone || '-'),
                vehicle: String(gatepass.vehicle_no || '-'),
                product: String(getProductsDisplay(gatepass.products) || '-'),
                quantity: String(Number(gatepass.total_quantity || 0).toLocaleString()),
                bundle: String(Number(gatepass.total_bundle || 0).toLocaleString()),
                checked: String(gatepass.checked_by || '-'),
                approved: String(gatepass.approved_by || '-')
            }));
            const shopName = String(currentShop?.shop_name || labels.title);
            const shopCode = String(currentShop?.shop_code || '-');
            const generated = new Date();
            const stamp = generated.toLocaleString('en-GB', { hour12: false });
            const reportId = `${getTodayDate().replace(/-/g, '')}-${String(generated.getTime()).slice(-6)}`;
            const text = await createNooriPdfText(doc,
                urdu || [shopName, shopCode, ...rows.flatMap(Object.values)].some(hasUrdu));
            const width = doc.internal.pageSize.getWidth();
            const height = doc.internal.pageSize.getHeight();
            const margin = 10;
            const usable = width - margin * 2;
            const color = /^#[0-9a-f]{6}$/i.test(currentTheme || '') ? currentTheme : '#2563eb';
            const accent = [1, 3, 5].map(index => parseInt(color.slice(index, index + 2), 16));
            const ink = [30, 41, 59];
            const muted = [85, 100, 120];
            const padding = 2.2;
            const weight = columns.reduce((sum, column) => sum + column.weight, 0);
            columns.forEach(column => { column.width = usable * column.weight / weight; });
            const ordered = urdu ? [...columns].reverse() : columns;
            const title = text.layout(shopName, { size: hasUrdu(shopName) ? 20 : 16, maxWidth: usable, bold: true });
            const subtitle = text.layout(labels.title, { size: urdu ? 14 : 10, maxWidth: usable });
            const half = (usable - 6) / 2;
            const metadata = (label, value) => {
                const caption = text.layout(`${label}:`, { size: urdu ? 10.5 : 8, maxWidth: half * 0.48 });
                const body = text.layout(value, { size: hasUrdu(value) ? 10.5 : 8,
                    maxWidth: half - caption.width - 2 });
                return { caption, body, height: Math.max(caption.height, body.height) };
            };
            const fields = [metadata(labels.total, String(rows.length)), metadata(labels.generated, stamp),
                metadata(labels.report, reportId), metadata(labels.code, shopCode)];
            const titleY = 10;
            const subtitleY = titleY + title.height + 2;
            const metaY = subtitleY + subtitle.height + 4;
            const metaRowHeight = [Math.max(fields[0].height, fields[1].height),
                Math.max(fields[2].height, fields[3].height)];
            const ruleY = metaY + metaRowHeight[0] + metaRowHeight[1] + 5;
            const tableTop = ruleY + 4;
            const drawField = (field, x, top) => {
                if (urdu) {
                    text.draw(field.caption, x + half - field.caption.width, top,
                        field.caption.width, 'right', muted);
                    text.draw(field.body, x, top, half - field.caption.width - 2, 'right', muted);
                } else {
                    text.draw(field.caption, x, top, field.caption.width, 'left', muted);
                    text.draw(field.body, x + field.caption.width + 2, top,
                        half - field.caption.width - 2, 'left', muted);
                }
            };
            const header = () => {
                text.draw(title, margin, titleY, usable, 'center', ink);
                text.draw(subtitle, margin, subtitleY, usable, 'center', muted);
                fields.forEach((field, index) => {
                    const logicalColumn = index % 2;
                    const visualColumn = urdu ? 1 - logicalColumn : logicalColumn;
                    drawField(field, margin + visualColumn * (half + 6),
                        metaY + (index > 1 ? metaRowHeight[0] + 2 : 0));
                });
                doc.setDrawColor(...accent);
                doc.setLineWidth(0.55);
                doc.line(margin, ruleY, width - margin, ruleY);
            };
            const headLayouts = Object.fromEntries(columns.map(column => [column.key,
                text.layout(column.title, { size: urdu ? (column.key === 'serial' ? 10.5 : 13) : (usable < 150 ? 7.5 : 8.5),
                    maxWidth: column.width - padding * 2, bold: !urdu })]));
            const headHeight = Math.max(...Object.values(headLayouts).map(block => block.height)) + padding * 2;
            const maxBodyHeight = height - 17 - tableTop - headHeight - padding * 2 - 1;
            if (maxBodyHeight < 12) throw new Error('The report header is too tall for this page size. Use a larger page or a shorter shop name.');

            // Rows are measured from HarfBuzz's positioned outlines. Oversized
            // cells are split into line fragments before AutoTable paginates them.
            const body = [];
            for (const row of rows) {
                const chunks = Object.fromEntries(columns.map(column => {
                    const value = row[column.key];
                    const layout = text.layout(value, {
                        size: hasUrdu(value) ? (usable < 150 ? 11.5 : 13) : (usable < 150 ? 7.5 : 8.5),
                        maxWidth: column.width - padding * 2
                    });
                    return [column.key, text.split(layout, maxBodyHeight)];
                }));
                const fragmentCount = Math.max(...Object.values(chunks).map(parts => parts.length));
                for (let fragment = 0; fragment < fragmentCount; fragment++) {
                    body.push(Object.fromEntries(columns.map(column => [column.key, {
                        content: '', nooriBlock: chunks[column.key][fragment] || text.layout(''),
                        nooriAlign: column.key === 'serial' ? 'center'
                            : text.baseDirection(row[column.key]) === 'rtl' ? 'right' : 'left'
                    }])));
                }
            }
            const cellBlocks = new WeakMap();
            drawPdfTable(doc, {
                columns: ordered.map(column => ({ header: column.title, dataKey: column.key })), body,
                startY: tableTop, margin: { top: tableTop, bottom: 17, left: margin, right: margin },
                tableWidth: usable, theme: 'grid', pageBreak: 'auto', rowPageBreak: 'avoid', showHead: 'everyPage',
                styles: { font: 'helvetica', fontSize: 1, cellPadding: padding, valign: 'middle',
                    lineWidth: 0.12, lineColor: [221, 227, 235], textColor: ink },
                headStyles: { fillColor: accent, textColor: [255, 255, 255] },
                alternateRowStyles: { fillColor: [246, 248, 251] },
                columnStyles: Object.fromEntries(columns.map(column => [column.key, { cellWidth: column.width }])),
                didParseCell: data => {
                    const block = data.section === 'head' ? headLayouts[data.column.dataKey] : data.cell.raw.nooriBlock;
                    const align = data.section === 'head' ? 'center' : data.cell.raw.nooriAlign;
                    cellBlocks.set(data.cell, { block, align });
                    data.cell.text = [''];
                    data.cell.styles.minCellHeight = block.height + padding * 2;
                },
                didDrawCell: data => {
                    const saved = cellBlocks.get(data.cell);
                    if (!saved) throw new Error('PDF cell layout was not preserved during pagination.');
                    text.draw(saved.block, data.cell.x + padding,
                        data.cell.y + (data.cell.height - saved.block.height) / 2,
                        data.cell.width - padding * 2, saved.align,
                        data.section === 'head' ? [255, 255, 255] : ink);
                },
                didDrawPage: header
            });
            const originalPages = doc.internal.getNumberOfPages();
            let first = 1;
            let last = originalPages;
            if (pdfPageRange === 'range') {
                first = Math.trunc(Number(pdfStartPage));
                last = Math.trunc(Number(pdfEndPage));
                if (!Number.isFinite(first) || !Number.isFinite(last) || first < 1 || last < first || first > originalPages) {
                    throw new Error(`Invalid page range. This report has ${originalPages} pages.`);
                }
                last = Math.min(last, originalPages);
                for (let page = originalPages; page > 0; page--) {
                    if (page < first || page > last) doc.deletePage(page);
                }
            }
            const pageCount = doc.internal.getNumberOfPages();
            const pageLabel = text.layout(labels.page, { size: urdu ? 10 : 8 });
            for (let page = 1; page <= pageCount; page++) {
                doc.setPage(page);
                doc.setDrawColor(221, 227, 235);
                doc.setLineWidth(0.12);
                doc.line(margin, height - 13, width - margin, height - 13);
                const number = text.layout(`${page} / ${pageCount}`, { size: 8, direction: 'ltr' });
                text.draw(number, width / 2 - 8, height - 9, 16, 'center', muted);
                text.draw(pageLabel, urdu ? width / 2 + 10 : width / 2 - 10 - pageLabel.width,
                    height - 9, pageLabel.width, 'left', muted);
                if (first !== 1 || last !== originalPages) {
                    text.draw(text.layout(`${first + page - 1} / ${originalPages}`, { size: 7 }),
                        width - margin - 24, height - 9, 24, 'right', muted);
                }
            }
            doc.setProperties({ title: labels.title, subject: shopName,
                author: user?.business_name || 'OurZest', creator: 'OurZest' });
            const safeShop = shopName.replace(/[<>:"/\\|?*\x00-\x1F]/g, '_').slice(0, 70);
            doc.save(`Gatepass_Report_${safeShop}_${getTodayDate()}.pdf`);
            showToast('success', t.reportGenerated || 'Report generated!');
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', `${t.pdfGenerationError || 'Error generating PDF'}: ${error.message || String(error)}`);
        } finally { setPdfLoading(false); }
    }, [filteredGatepasses, currentLanguage, pdfOrientation, pdfPageSize, pdfPageRange,
        pdfStartPage, pdfEndPage, t, currentShop, user, showToast, currentTheme,
        getProductsDisplay, formatDate, getTodayDate]);


    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} isRTL={isRTL} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                t={t}
                loading={loadingShops}
                isRTL={isRTL}
            />

            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>{t.gatepassList}</h1>
                            <p>{t.formDetails}</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                        <button
                            className="ozlistui-header-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? (t.hideFilters || 'Hide Filters') : (t.filters || 'Filters')}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? (t.hideFilters || 'Hide Filters') : (t.filters || 'Filters')}</span>
                        </button>

                        {!currentShop && (
                            <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{t.switchShop}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="ozlistui-filters-section">
                    <div className="ozlistui-filters-row">
                        <CustomDropdown
                            label={t.filterBy || 'Filter By'}
                            icon={faTag}
                            value={filterBy}
                            onChange={setFilterBy}
                            options={filterByOptions}
                            t={t}
                            isRTL={isRTL}
                        />

                        {filterBy === 'serial' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="serialFilter"
                                        value={serialFilter}
                                        onChange={(e) => setSerialFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="serialFilter">
                                        <FontAwesomeIcon icon={faHashtag} />
                                        {t.serialFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'party' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="partyFilter"
                                        value={partyFilter}
                                        onChange={(e) => setPartyFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="partyFilter">
                                        <FontAwesomeIcon icon={faUsers} />
                                        {t.partyFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'driver' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="driverFilter"
                                        value={driverFilter}
                                        onChange={(e) => setDriverFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="driverFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {t.driverFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'phone' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="tel"
                                        id="phoneFilter"
                                        value={phoneFilter}
                                        onChange={(e) => setPhoneFilter(e.target.value)}
                                        placeholder=" "
                                        dir="ltr"
                                    />
                                    <label htmlFor="phoneFilter">
                                        <FontAwesomeIcon icon={faPhone} />
                                        {t.phoneFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'vehicle' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="vehicleFilter"
                                        value={vehicleFilter}
                                        onChange={(e) => setVehicleFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="vehicleFilter">
                                        <FontAwesomeIcon icon={faTruck} />
                                        {t.vehicleFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'product' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="productFilter"
                                        value={productFilter}
                                        onChange={(e) => setProductFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="productFilter">
                                        <FontAwesomeIcon icon={faTag} />
                                        {t.productFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'date' && (
                            <>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="startDate"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            max={getTodayDate()}
                                            placeholder=" "
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="startDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {t.startDate}
                                        </label>
                                    </div>
                                </div>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="endDate"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            min={startDate}
                                            max={getTodayDate()}
                                            placeholder=" "
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="endDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {t.endDate}
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        {filterBy === 'added_by' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="addedByFilter"
                                        value={addedByFilter}
                                        onChange={(e) => setAddedByFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="addedByFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {t.addedByFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        <CustomDropdown
                            label={t.orientation || 'Orientation'}
                            icon={faFilePdf}
                            value={pdfOrientation}
                            onChange={setPdfOrientation}
                            options={pdfOrientationOptions}
                            t={t}
                            isRTL={isRTL}
                        />

                        <CustomDropdown
                            label={t.pageSize || 'Page Size'}
                            icon={faFilePdf}
                            value={pdfPageSize}
                            onChange={setPdfPageSize}
                            options={pdfPageSizeOptions}
                            t={t}
                            isRTL={isRTL}
                        />

                        <div className="ozlistui-filter-group">
                            <button onClick={fetchGatepasses} className="ozlistui-refresh-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faRefresh} />
                                <span className="ozlistui-btn-text">{t.refresh}</span>
                            </button>
                        </div>

                        <div className="ozlistui-filter-group">
                            <button
                                onClick={exportToPDF}
                                className="ozlistui-refresh-btn"
                                disabled={filteredGatepasses.length === 0 || pdfLoading}
                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                            >
                                {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                <span className="ozlistui-btn-text">{t.exportToPDF}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>{t.loadingUserData}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{t.noShopSelected}</h3>
                        <p>{t.selectShop}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{t.switchShop}</span>
                        </button>
                    </div>
                </div>
            ) : filteredGatepasses.length === 0 ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faTruck} />
                        <h3>{t.noGatepasses || 'No gate passes found'}</h3>
                        <p>{t.searchPlaceholder || 'Try adjusting your search or filters'}</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="ozlistui-table-scroll">
                        <div className="ozlistui-table-container">
                            <div className="ozlistui-table-wrapper">
                                <table className="ozlistui-table">
                                    <thead>
                                        <tr>
                                            <th className="ozlistui-id-column ozlistui-centered-cell">{t.serialNo}</th>
                                            <th className="ozlistui-centered-cell">{t.serial}</th>
                                            <th className="ozlistui-centered-cell">{t.party}</th>
                                            <th className="ozlistui-centered-cell">{t.driver}</th>
                                            <th className="ozlistui-centered-cell">{t.phone}</th>
                                            <th className="ozlistui-centered-cell">{t.vehicle}</th>
                                            <th className="ozlistui-centered-cell">{t.product}</th>
                                            <th className="ozlistui-centered-cell">{t.quantity}</th>
                                            <th className="ozlistui-centered-cell">{t.bundle}</th>
                                            <th className="ozlistui-centered-cell">{t.checked}</th>
                                            <th className="ozlistui-centered-cell">{t.approved}</th>
                                            <th className="ozlistui-actions-column ozlistui-centered-cell">{t.actions}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagination.currentGatepasses.map((gatepass, index) => {
                                            const addedBy = gatepass.username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            return (
                                                <tr key={gatepass.gatepass_id} onClick={() => handleView(gatepass)}>
                                                    <td className="ozlistui-id-cell ozlistui-centered-cell" data-label={t.serialNo}>
                                                        <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.serial}>
                                                        <span className="ozlistui-cell-text ozlistui-serial-number" dir={isRTL ? 'rtl' : 'ltr'}><strong>{gatepass.serial_number || '-'}</strong></span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.party}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{gatepass.party_name || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.driver}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{gatepass.driver_name || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.phone}>
                                                        <span className="ozlistui-cell-text" dir="ltr">{gatepass.driver_phone || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.vehicle}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{gatepass.vehicle_no || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.product}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{getProductsDisplay(gatepass.products)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.quantity}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{Number(gatepass.total_quantity || 0).toLocaleString()}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.bundle}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{Number(gatepass.total_bundle || 0).toLocaleString()}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.checked}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{gatepass.checked_by || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.approved}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{gatepass.approved_by || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-actions-cell ozlistui-centered-cell" data-label={t.actions}>
                                                        <div className="ozlistui-actions-content">
                                                            <button
                                                                onClick={(event) => { event.stopPropagation(); handleView(gatepass); }}
                                                                className="ozlistui-action-btn ozlistui-view-btn"
                                                                title={t.view}
                                                                aria-label={`${t.view} ${gatepass.serial_number || ''}`}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={(event) => { event.stopPropagation(); handlePrint(gatepass); }}
                                                                className="ozlistui-action-btn ozlistui-print-btn"
                                                                title={t.print}
                                                                aria-label={`${t.print} ${gatepass.serial_number || ''}`}
                                                            >
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                            <button
                                                                onClick={(event) => { event.stopPropagation(); handleEdit(gatepass); }}
                                                                className="ozlistui-action-btn ozlistui-edit-btn"
                                                                title={t.edit}
                                                                aria-label={`${t.edit} ${gatepass.serial_number || ''}`}
                                                                disabled={!isSelfAdded}
                                                                style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={(event) => { event.stopPropagation(); handleDeleteClick(gatepass.gatepass_id); }}
                                                                className="ozlistui-action-btn ozlistui-delete-btn"
                                                                title={t.delete}
                                                                aria-label={`${t.delete} ${gatepass.serial_number || ''}`}
                                                                disabled={!isSelfAdded}
                                                                style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {filteredGatepasses.length > 0 && currentShop && (
                        <div className="ozlistui-pagination">
                            <div className="ozlistui-pagination-info">
                                <span>{t.showing} {pagination.startIndex + 1} {t.of} {filteredGatepasses.length} {t.gatepasses}</span>
                            </div>
                            <div className="ozlistui-pagination-controls">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="ozlistui-pagination-btn">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{t.previous}</span>
                                </button>
                                <div className="ozlistui-page-numbers">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="ozlistui-pagination-btn">
                                    <span>{t.next}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedGatepass && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faTruck} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{t.gatepassDetails}</h3>
                                <span className="ozlistui-gatepass-number-badge">#{selectedGatepass.serial_number}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozlistui-modal-body ozlistui-view-modal-body">
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faHashtag} />{t.gatepassSummary}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.serial}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}><strong>{selectedGatepass.serial_number || '-'}</strong></span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.party}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedGatepass.party_name || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.selection}:</span><span className="ozlistui-view-value">{selectedGatepass.selection_mode === 'single' ? t.single : t.multiple}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.quantity}:</span><span className="ozlistui-view-value" dir="ltr">{Number(selectedGatepass.total_quantity || 0).toLocaleString()}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.bundle}:</span><span className="ozlistui-view-value" dir="ltr">{Number(selectedGatepass.total_bundle || 0).toLocaleString()}</span></div>
                                </div>
                            </div>
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faUser} />{t.contactInfo}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.driver}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedGatepass.driver_name || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.phone}:</span><span className="ozlistui-view-value" dir="ltr">{selectedGatepass.driver_phone || '-'}</span></div>
                                </div>
                            </div>
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faTruck} />{t.vehicleInfo}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.vehicle}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedGatepass.vehicle_no || '-'}</span></div>
                                </div>
                            </div>
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faTag} />{t.product}</h4>
                                <div className="ozlistui-remarks-box" dir={isRTL ? 'rtl' : 'ltr'}>
                                    {(selectedGatepass.products || []).map(p => p.product_name).filter(Boolean).join(', ') || t.noProduct}
                                </div>
                            </div>
                            <div className="ozlistui-view-section ozlistui-metadata-section">
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.checked}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedGatepass.checked_by || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.approved}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedGatepass.approved_by || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.createdBy}:</span><span className="ozlistui-view-value"><span className="ozlistui-added-by-badge" dir={isRTL ? 'rtl' : 'ltr'}>{selectedGatepass.username || '-'}</span></span></div>
                                    {selectedGatepass.created_at && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.dateTime}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{formatDateTime(selectedGatepass.created_at)}</span></div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{t.close}</button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(selectedGatepass); }}>
                                <FontAwesomeIcon icon={faEdit} />
                                {t.edit}
                            </button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { handlePrint(selectedGatepass); }}>
                                <FontAwesomeIcon icon={faPrint} />
                                {t.print}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedGatepass && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div
                        ref={modalRef}
                        className="ozlistui-modal-content ozlistui-edit-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                    >
                        <div className="ozlistui-resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="ozlistui-resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="ozlistui-resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="ozlistui-resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="ozlistui-resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="ozlistui-resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="ozlistui-resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="ozlistui-resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        <div className="ozlistui-modal-header" onMouseDown={startDrag}>
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{t.editGatepass}</h3>
                                <span className="ozlistui-gatepass-number-badge">#{selectedGatepass.serial_number}</span>
                            </div>
                            <div className="ozlistui-width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button key={key} className={`ozlistui-width-btn ${currentWidthMode === key ? 'ozlistui-active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label}>
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateGatepass}>
                            <div className="ozlistui-modal-body">
                                <div className="ozlistui-form-grid">
                                    <div className={`ozlistui-form-group ${editFormErrors.serial_number ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_serial_number"
                                                name="serial_number"
                                                value={editFormData.serial_number}
                                                onChange={(e) => setEditFormData({ ...editFormData, serial_number: e.target.value })}
                                                placeholder=" "
                                                className={editFormErrors.serial_number ? 'ozlistui-error' : ''}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_serial_number">
                                                <FontAwesomeIcon icon={faHashtag} />
                                                {t.serial} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.serial_number && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.serial_number}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_party_name"
                                                name="party_name"
                                                value={editFormData.party_name}
                                                onChange={(e) => setEditFormData({ ...editFormData, party_name: e.target.value })}
                                                placeholder=" "
                                                readOnly
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_party_name">
                                                <FontAwesomeIcon icon={faUsers} />
                                                {t.party}
                                            </label>
                                        </div>
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.driver_name ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_driver_name"
                                                name="driver_name"
                                                value={editFormData.driver_name}
                                                onChange={(e) => setEditFormData({ ...editFormData, driver_name: e.target.value })}
                                                placeholder=" "
                                                className={editFormErrors.driver_name ? 'ozlistui-error' : ''}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_driver_name">
                                                <FontAwesomeIcon icon={faUser} />
                                                {t.driver} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.driver_name && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.driver_name}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.driver_phone ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="tel"
                                                id="edit_driver_phone"
                                                name="driver_phone"
                                                value={editFormData.driver_phone}
                                                onChange={(e) => setEditFormData({ ...editFormData, driver_phone: e.target.value })}
                                                placeholder=" "
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_driver_phone">
                                                <FontAwesomeIcon icon={faPhone} />
                                                {t.phone}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                        {editFormErrors.driver_phone && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.driver_phone}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.vehicle_no ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_vehicle_no"
                                                name="vehicle_no"
                                                value={editFormData.vehicle_no}
                                                onChange={(e) => setEditFormData({ ...editFormData, vehicle_no: e.target.value })}
                                                placeholder=" "
                                                className={editFormErrors.vehicle_no ? 'ozlistui-error' : ''}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_vehicle_no">
                                                <FontAwesomeIcon icon={faTruck} />
                                                {t.vehicle} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.vehicle_no && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.vehicle_no}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <select
                                                id="edit_selection_mode"
                                                name="selection_mode"
                                                value={editFormData.selection_mode}
                                                onChange={(e) => setEditFormData(prev => ({
                                                    ...prev,
                                                    selection_mode: e.target.value,
                                                    selected_products: e.target.value === 'single'
                                                        ? prev.selected_products.slice(0, 1)
                                                        : prev.selected_products
                                                }))}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            >
                                                <option value="single">{t.single}</option>
                                                <option value="multiple">{t.multiple}</option>
                                            </select>
                                            <label htmlFor="edit_selection_mode">
                                                <FontAwesomeIcon icon={faTag} />
                                                {t.selection}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="number"
                                                step="0.001"
                                                id="edit_total_quantity"
                                                name="total_quantity"
                                                value={editFormData.total_quantity}
                                                onChange={(e) => setEditFormData({ ...editFormData, total_quantity: e.target.value })}
                                                placeholder=" "
                                                min="0"
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_total_quantity">
                                                <FontAwesomeIcon icon={faHashtag} />
                                                {t.quantity}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="number"
                                                step="0.001"
                                                id="edit_total_bundle"
                                                name="total_bundle"
                                                value={editFormData.total_bundle}
                                                onChange={(e) => setEditFormData({ ...editFormData, total_bundle: e.target.value })}
                                                placeholder=" "
                                                min="0"
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_total_bundle">
                                                <FontAwesomeIcon icon={faHashtag} />
                                                {t.bundle}
                                            </label>
                                        </div>
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.checked_by ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_checked_by"
                                                name="checked_by"
                                                value={editFormData.checked_by}
                                                onChange={(e) => setEditFormData({ ...editFormData, checked_by: e.target.value })}
                                                placeholder=" "
                                                className={editFormErrors.checked_by ? 'ozlistui-error' : ''}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_checked_by">
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                {t.checked} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.checked_by && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.checked_by}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.approved_by ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_approved_by"
                                                name="approved_by"
                                                value={editFormData.approved_by}
                                                onChange={(e) => setEditFormData({ ...editFormData, approved_by: e.target.value })}
                                                placeholder=" "
                                                className={editFormErrors.approved_by ? 'ozlistui-error' : ''}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_approved_by">
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                {t.approved} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.approved_by && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.approved_by}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ozlistui-full-width ${editFormErrors.selected_products ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_product_search"
                                                value={editProductSearch}
                                                onChange={(e) => setEditProductSearch(e.target.value)}
                                                placeholder=" "
                                                autoComplete="off"
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_product_search">
                                                <FontAwesomeIcon icon={faTag} />
                                                {t.searchProduct} ({editFormData.selected_products.length} {t.selected})
                                            </label>
                                        </div>
                                        <div className="ozlistui-product-dropdown">
                                            {filteredEditProducts.length ? (
                                                filteredEditProducts.map(product => (
                                                    <div
                                                        key={product.id}
                                                        className={`ozlistui-product-option ${editFormData.selected_products.includes(Number(product.id)) ? 'ozlistui-selected' : ''}`}
                                                        onClick={() => toggleEditProduct(Number(product.id))}
                                                    >
                                                        <input
                                                            type={editFormData.selection_mode === 'single' ? 'radio' : 'checkbox'}
                                                            name="edit-gatepass-product"
                                                            checked={editFormData.selected_products.includes(Number(product.id))}
                                                            readOnly
                                                        />
                                                        <span>{product.product_name}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="ozlistui-product-empty">{t.noProducts}</div>
                                            )}
                                        </div>
                                        {editFormErrors.selected_products && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.selected_products}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="ozlistui-modal-footer">
                                <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                    {t.cancel}
                                </button>
                                <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading}>
                                    {editLoading ? (
                                        <>
                                            <div className="ozlistui-btn-spinner"></div>
                                            <span>{t.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>{t.saveChanges}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                        <div className="ozlistui-modal-header ozlistui-delete-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="ozlistui-modal-title"><h3>{t.confirmDelete}</h3></div>
                            <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-delete-message">
                                <p>{t.deleteMessage}</p>
                                <p><FontAwesomeIcon icon={faExclamationTriangle} /> {t.deleteWarning}</p>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{t.noCancel}</button>
                            <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{t.yesDelete}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GatepassList;